import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactSelect, { components } from "react-select";
import Loader from "../../common/components/Loader";
import CreatableSelect from "react-select/creatable";
import { assetSurveyStatus, recommendationSurveyStatus } from "../../assetSurveyProject/constants";
import { useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { sanitizeHtml } from "../../common/components/SafeHtml";

export default function SelectBox({
    label,
    optionsList,
    handleChange,
    value,
    showErrorBorder,
    disabled,
    className,
    isBuilding,
    hasEmptySelect = true,
    isRecommendation = false,
    isCapitalType = false,
    isClearable = true,
    isSurveyor = false,
    isDynamicField = false,
    isAsset = false,
    isLoading = false,
    isGrouping = false,
    handleInputChange,
    isManufacturer = false,
    isLoadingDrop = false,
    manufacturerCount = null,
    isCreatable = false,
    createOption,
    isAssetSurveyProject = false,
    placeholder,
    menuPosition: menuPos = "absolute",
    emptyValue = "",
    isStatus = false,
    hasGroupedByOptions = false,
    rowId = null,
    isSurveyAssetField = false,
    isDropdownLineEdit = false,
    handleCellFocus,
    keyItem = "",
    isDropdownLoading = false,
    rowData,
    rowIndex,
    config,
    isPeriodicExport = false,
    isRecommendationTemplate = false,
    isDropdownDynamic = false,
    nameValue = null,
    isMulti = false,
    isAssetStatus = false,
    isBoolean = false,
    hideLabel = false,
    hasSearchFunction = false,
    isRecomSurveyProject = false,
    isMobileRecommendation = false,
    isRecomTemplate = false,
    hasCallOnMenuOpen = false,
    parentClassName = "",
    isSurveyDefaultValue = false,
    isPriority = false,
    isRecommendationForm = false,
    skipFormControlClass = false,
    hasDisabledOptions = false,
    hasMaxHeight = "",
    showCount = false
}) {
    const { updateSurveyAssetResponseLoading } = useSelector(state => state.assetSurveyProjectReducer);
    const { updateMobileRecommendationFieldsResponseLoading } = useSelector(state => state.mobileRecommendationsReducer);
    const tooltipId = useRef(`priority-tip-${Math.random().toString(36).substr(2, 9)}`);
    const defaultOptions = useMemo(() => {
        let options = [];
        if (hasGroupedByOptions) options = optionsList;
        else
            optionsList?.length > 0 &&
                optionsList.forEach(option => {
                    options.push({
                        label: isSurveyDefaultValue
                            ? (option.name ?? "")
                            : isSurveyDefaultValue
                              ? (option.name ?? "")
                              : `${!(isCapitalType || isSurveyor || isDynamicField) ? option.name : ""} ${isDynamicField ? option.value : ""} ${
                                    isBuilding &&
                                    (isRecommendationForm ? option?.building_description : option?.description || option?.building_description)
                                        ? `(${isRecommendationForm ? option.building_description : option.building_description || option.description})`
                                        : ""
                                }${
                                    isRecommendationForm && isBuilding && option?.description ? ` (${option.description})` : ""
                                } ${isCapitalType ? option.display_name : ""}${isSurveyor ? option.printed_name || option.name : ""}`,
                        value: isDynamicField || isGrouping ? option.value : option.id,
                        isDisabled: (isStatus || hasDisabledOptions) && option?.disabled,
                        ...(option?.hasSeparator && { hasSeparator: true }),
                        ...(isPriority && { description: option.description })
                    });
                });
        if (hasEmptySelect) {
            options = [{ label: placeholder ?? "Select", value: emptyValue ?? "" }, ...options];
        }
        return options;
    }, [
        hasEmptySelect,
        placeholder,
        emptyValue,
        hasGroupedByOptions,
        optionsList,
        isSurveyDefaultValue,
        isCapitalType,
        isSurveyor,
        isDynamicField,
        isBuilding,
        isGrouping,
        isStatus,
        isPriority
    ]);
    const onChange = values => {
        if (isPeriodicExport && isMulti) {
            const selectedValues = values?.map(item => item.value)?.filter(value => value !== "");
            handleChange(selectedValues);
        } else {
            if (values?.value !== value) {
                if (isManufacturer || isDropdownLineEdit || isDropdownDynamic || isSurveyDefaultValue) {
                    handleChange(values?.value ?? "", isDropdownDynamic ? values?.key || "" : values?.label || "");
                } else {
                    handleChange(values?.value ?? "");
                }
            }
        }
    };
    const [menuPosition, setMenuPosition] = useState("right");
    const selectRef = useRef(null);

    const [isCreating, setIsCreating] = useState(false);
    const [options, setOptions] = useState(defaultOptions);

    useEffect(() => {
        setOptions(defaultOptions);
    }, [defaultOptions]);

    const handleCreate = async inputValue => {
        setIsCreating(true);
        const newOption = await createOption(inputValue);
        setIsCreating(false);
        setOptions(prev => [...prev, newOption]);
    };
    const filterOptionByLabel = (option, inputValue) => {
        if (!inputValue) return true;
        return String(option?.label ?? "")
            ?.toLowerCase()
            ?.includes(inputValue?.toLowerCase());
    };
    const [isMenuOPen, setIsMenuOpen] = useState(false);

    const flattenedGroupOptions = safeArray(optionsList)
        .flatMap(g => safeArray(g?.options).map(normalizeOption))
        .filter(Boolean);

    const normalizedOptions = safeArray(options).map(normalizeOption).filter(Boolean);

    const nVal = normalizeIncoming(value);

    const selectedGroupOption =
        hasGroupedByOptions &&
        flattenedGroupOptions.find(o => equalsNormalized(o.value, nVal) || (typeof o.label === "string" && o.label.trim() === String(nVal)));

    const selectedValue = isDropdownLineEdit
        ? hasGroupedByOptions && keyItem === "status"
            ? selectedGroupOption || { value: String(nameValue ?? ""), label: String(nameValue ?? "") }
            : normalizedOptions.find(
                  option => equalsNormalized(option?.value, nVal) || (typeof option?.label === "string" && option?.label?.trim() === String(nVal))
              ) || { value: String(nVal), label: String(nVal) }
        : hasGroupedByOptions
          ? (selectedGroupOption ??
            (isSurveyAssetField ? { label: placeholder ?? "Select", value: emptyValue ?? "" } : isRecomTemplate ? null : undefined))
          : normalizedOptions.find(option => equalsNormalized(option?.value, nVal));

    useEffect(() => {
        if (selectRef?.current && (isRecommendation || isMobileRecommendation)) {
            const parentDiv = document?.querySelector(
                isMobileRecommendation ? ".asset-edit-port" : isSurveyAssetField ? ".asset-multi-edit" : ".height-new-cp"
            );
            const parentRect = parentDiv?.getBoundingClientRect();
            const selectRect = selectRef?.current?.getBoundingClientRect();
            const spaceOnLeft = selectRect?.left - parentRect?.left;
            const spaceOnRight = parentRect?.right - selectRect?.right;
            const dropdownMenu = document?.querySelector(".react-select__menu");
            if (dropdownMenu) {
                const dropdownMenuWidth = dropdownMenu?.getBoundingClientRect()?.width;
                if (spaceOnRight >= dropdownMenuWidth) {
                    setMenuPosition("right");
                } else if (spaceOnLeft >= dropdownMenuWidth) {
                    setMenuPosition("left");
                } else if (spaceOnRight > 0 || spaceOnRight > 0) {
                    setMenuPosition(spaceOnRight > spaceOnLeft ? "right" : "left");
                }
            }
        }
    }, [isMenuOPen, isMobileRecommendation, isRecommendation, isSurveyAssetField]);

    useEffect(() => {
        if (isPriority && !isMenuOPen) {
            ReactTooltip.hide();
        }
        return () => {
            if (isPriority) {
                ReactTooltip.hide();
            }
        };
    }, [isMenuOPen, isPriority]);

    const getSeparatorStyle = backgroundColor => ({
        borderTop: "1px solid #b8b4b4",
        marginTop: "3px",
        backgroundColor: "transparent",
        backgroundImage:
            backgroundColor && backgroundColor !== "transparent"
                ? `linear-gradient(to bottom, transparent 0, transparent 3px, ${backgroundColor} 3px, ${backgroundColor} 100%)`
                : "none"
    });

    const customStyles = {
        menu: (provided, state) => ({
            ...provided,
            left: menuPosition === "left" ? "auto" : 0,
            right: menuPosition === "right" ? "auto" : 0
        }),
        menuList: provided => ({
            ...provided,
            ...(hasMaxHeight ? { maxHeight: hasMaxHeight } : {})
        }),
        option: (provided, state) => ({
            ...provided,
            ...(hasDisabledOptions && {
                color: state?.isDisabled ? "gray" : provided?.color,
                cursor: state?.isDisabled ? "not-allowed" : "pointer"
            }),
            ...(state?.data?.hasSeparator && getSeparatorStyle(provided?.backgroundColor)),
            ...(hasGroupedByOptions && { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })
        })
    };

    let menuPlacement = {};
    let menuPortalTarget = {};
    if (isSurveyAssetField)
        menuPlacement = {
            menuPlacement: "auto"
        };
    if (isAssetSurveyProject && !hasGroupedByOptions && !isRecomSurveyProject)
        menuPortalTarget = {
            menuPortalTarget: document.body
        };

    return (
        <div
            className={`${isRecommendationTemplate ? "" : "form-group"} ${isDropdownLineEdit || isStatus ? "categ-drop-rc mb-0" : ""} ${
                isStatus ? "mb-0" : ""
            } ${parentClassName}`}
        >
            {isPriority && (
                <ReactTooltip
                    id={tooltipId.current}
                    effect="solid"
                    place="right"
                    backgroundColor="#007bff"
                    className="rc-tooltip-custom-class rich-text-tooltip"
                    html={true}
                    getContent={dataTip => (dataTip ? sanitizeHtml(dataTip) : "")}
                />
            )}
            {!isStatus && !isDropdownLineEdit && !hideLabel && (
                <label className={isAssetSurveyProject || isRecomSurveyProject ? "form-label" : ""}>{label}</label>
            )}
            <div className={`selectOtr ${className} ${disabled ? " cursor-diabled " : ""}`} ref={selectRef}>
                {isCreatable ? (
                    <CreatableSelect
                        className={`${isRecommendation || isAsset || isGrouping || skipFormControlClass ? "" : "form-control"} ${disabled ? " cursor-diabled " : ""}${
                            showErrorBorder ? "error-border" : ""
                        }`}
                        options={options}
                        value={selectedValue}
                        isClearable={isClearable && hasEmptySelect}
                        onChange={onChange}
                        classNamePrefix="react-select"
                        isDisabled={disabled || isCreating}
                        components={{
                            MenuList: props => <MenuList {...props} />,
                            ...(isPriority && {
                                Option: props => (
                                    <components.Option
                                        {...props}
                                        innerProps={{
                                            ...props.innerProps,
                                            "data-tip": props.data.description || "",
                                            "data-for": tooltipId.current,
                                            "data-tip-disable": !props.data.description,
                                            "data-place": "right",
                                            onMouseEnter: e => {
                                                props.innerProps?.onMouseEnter?.(e);
                                                if (props.data.description) {
                                                    ReactTooltip.show(e.currentTarget);
                                                }
                                            },
                                            onMouseLeave: e => {
                                                props.innerProps?.onMouseLeave?.(e);
                                                ReactTooltip.hide();
                                            }
                                        }}
                                    />
                                )
                            })
                        }}
                        isLoading={isLoading || isLoadingDrop || isCreating}
                        onCreateOption={handleCreate}
                        onMenuOpen={() => setIsMenuOpen(true)}
                        onMenuClose={() => setIsMenuOpen(false)}
                    />
                ) : (
                    <ReactSelect
                        className={`${
                            isRecommendation ||
                            isAsset ||
                            isGrouping ||
                            isDropdownLineEdit ||
                            isAssetSurveyProject ||
                            isStatus ||
                            isRecomSurveyProject ||
                            skipFormControlClass
                                ? ""
                                : "form-control"
                        } ${disabled ? " cursor-diabled " : ""}${showErrorBorder ? "error-border" : ""}`}
                        options={options?.length !== 0 && !isDropdownLoading ? options : []}
                        value={selectedValue}
                        isClearable={isClearable && hasEmptySelect}
                        onChange={onChange}
                        classNamePrefix="react-select"
                        isDisabled={disabled || (isStatus && (updateSurveyAssetResponseLoading || updateMobileRecommendationFieldsResponseLoading))}
                        filterOption={filterOptionByLabel}
                        components={{
                            MenuList: props => (
                                <MenuList
                                    {...props}
                                    count={manufacturerCount}
                                    isManufacturer={isManufacturer || hasSearchFunction}
                                    hasSearchFunction={hasSearchFunction}
                                    emptyValue={emptyValue}
                                    isLoading={isLoading || isLoadingDrop}
                                    isDropdownLoading={isDropdownLoading}
                                    hasCallOnMenuOpen={hasCallOnMenuOpen}
                                    showCount={showCount}
                                />
                            ),
                            Option: isPriority
                                ? props => (
                                      <components.Option
                                          {...props}
                                          innerProps={{
                                              ...props.innerProps,
                                              "data-tip": props.data.description || "",
                                              "data-for": tooltipId.current,
                                              "data-tip-disable": !props.data.description,
                                              "data-place": "right",
                                              onMouseEnter: e => {
                                                  props.innerProps?.onMouseEnter?.(e);
                                                  if (props.data.description) {
                                                      ReactTooltip.show(e.currentTarget);
                                                  }
                                              },
                                              onMouseLeave: e => {
                                                  props.innerProps?.onMouseLeave?.(e);
                                                  ReactTooltip.hide();
                                              }
                                          }}
                                      />
                                  )
                                : components.Option
                        }}
                        isLoading={
                            isLoading ||
                            (isStatus && (updateSurveyAssetResponseLoading === rowId || updateMobileRecommendationFieldsResponseLoading === rowId)) ||
                            isDropdownLoading
                        }
                        onInputChange={
                            isManufacturer || hasSearchFunction
                                ? (...args) => {
                                      if (isDropdownLineEdit && hasSearchFunction) {
                                          handleCellFocus(keyItem, rowIndex, rowData?.id, config[keyItem]?.isDropdownLineEdit, rowData, args[0]);
                                      }
                                      return handleInputChange && handleInputChange(...args, keyItem);
                                  }
                                : ""
                        }
                        onFocus={event => {
                            if (isDropdownLineEdit) {
                                handleCellFocus(keyItem, rowIndex, rowData?.id, config[keyItem]?.isDropdownLineEdit, rowData);
                            } else if (hasCallOnMenuOpen) {
                                handleInputChange();
                            } else if (isManufacturer || hasSearchFunction) {
                                handleInputChange(event.target.value, { action: "input-click" }, keyItem);
                            }
                        }}
                        styles={
                            isRecommendation && !isAsset
                                ? customStyles
                                : {
                                      ...customStyles,
                                      singleValue: (provided, state) => ({
                                          ...provided,
                                          ...(isStatus && {
                                              color: (isAssetStatus ? assetSurveyStatus : recommendationSurveyStatus)?.[state?.data?.value]?.color
                                          }),
                                          ...(hasGroupedByOptions && { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })
                                      }),
                                      option: (provided, state) => ({
                                          ...provided,
                                          ...(isStatus && {
                                              color: state?.isDisabled ? "gray" : provided?.color,
                                              cursor: state?.isDisabled ? "not-allowed" : "pointer",
                                              backgroundColor: state?.isDisabled && state?.isSelected ? "white" : provided.backgroundColor
                                          }),
                                          ...(hasGroupedByOptions && { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })
                                      }),
                                      menuPortal: provided => ({
                                          ...provided,
                                          ...((isStatus || isSurveyAssetField) && {
                                              ...(isStatus && { width: "180px" })
                                              //   "@media only screen and (max-width: 1679px)": {
                                              //       ...provided["@media only screen and (max-width: 1679px)"],
                                              //       zoom: "93%"
                                              //   }
                                          }),
                                          zIndex: 999
                                      })
                                  }
                        }
                        menuPosition={menuPos}
                        onMenuOpen={() => {
                            setIsMenuOpen(true);
                        }}
                        onMenuClose={() => {
                            setIsMenuOpen(false);
                        }}
                        // menuIsOpen
                        isMulti={isMulti ? true : false}
                        {...menuPlacement}
                        {...menuPortalTarget}
                    />
                )}
            </div>
        </div>
    );
}

const MenuList = props => {
    const menuListRef = useRef();
    const isLocalSearch = props?.showCount && !props?.hasSearchFunction && props?.selectProps?.inputValue;
    const visibleOptionsCount = React.Children.toArray(props.children).filter(
        child => child?.props?.data && !equalsNormalized(child?.props?.data?.value, props?.emptyValue ?? "")
    ).length;
    const displayCount = isLocalSearch ? visibleOptionsCount : props?.count;

    useEffect(() => {
        if (menuListRef.current) {
            menuListRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, []);
    return (
        <components.MenuList {...props} innerRef={menuListRef}>
            {props?.isLoading && (props?.isManufacturer || props?.hasCallOnMenuOpen) ? (
                <div className="col-md-12 p-4">
                    <Loader />
                </div>
            ) : (
                <>
                    {(props?.isManufacturer || props?.showCount) && displayCount != null && <div className="option-count">Count: {displayCount}</div>}
                    {props.children}
                </>
            )}
        </components.MenuList>
    );
};

const safeArray = x => (Array.isArray(x) ? x : []);
const normalizeIncoming = v => {
    if (v == null) return "";
    if (typeof v === "object") return v.id ?? v.name ?? String(v);
    return v;
};
const normalizeOption = o => {
    if (o == null) return null;
    const val = o?.value ?? o?.id ?? o;
    const lbl = typeof o?.label === "string" ? o.label : o?.name != null ? String(o.name) : String(val ?? "");
    return { value: val, label: lbl };
};
const equalsNormalized = (optVal, incoming) => String(optVal ?? "") === String(incoming ?? "");
